import { useCallback } from 'react';
import { Node } from 'reactflow';
import { TableRowData } from '@/shared/types/canvas';
import { DockerBuildContract } from '../types';
import { FlowNodeData } from '../../../tabs/requirements/components/canvas/FlowNode';
import { useAuth } from '../../../../system/auth/hooks/useAuth';
import { TokenManager } from '@/shared/lib/apiClient';
import { getFromStorage, getProjectGitSettingsFromStorage } from '@/shared/lib/storageUtils';
import { getLatestBuild } from '@/shared/lib/buildStatusTracker';

/** Ordered LLR UIIDs from epic requirements (`llr_1`, `llr_2`, …). */
function orderedLlrUiidsFromEpicRequirements(highLevelRequirements: Record<string, unknown>): string[] {
    const llrMap = highLevelRequirements?.low_level_requirements as Record<string, { uiid?: string }> | undefined;
    if (!llrMap || typeof llrMap !== 'object') return [];
    return Object.keys(llrMap)
        .sort((a, b) => {
            const na = parseInt(String(a).replace(/^.*?(\d+)$/, '$1'), 10) || 0;
            const nb = parseInt(String(b).replace(/^.*?(\d+)$/, '$1'), 10) || 0;
            return na - nb;
        })
        .map((k) => llrMap[k]?.uiid)
        .filter((uiid): uiid is string => typeof uiid === 'string' && uiid.trim().length > 0);
}

/**
 * Epic `requirements.start_at`: LLR uiid to resume from.
 * 1) First LLR in epic order whose latest build is Error or In Progress.
 * 2) If none, the first LLR (e.g. all Completed → restart from the beginning).
 */
function computeEpicStartAt(projectId: string | null, orderedLlrUiids: string[]): string | undefined {
    if (!projectId || orderedLlrUiids.length === 0) return undefined;
    const firstLlrUiid = orderedLlrUiids[0];
    for (const id of orderedLlrUiids) {
        const st = getLatestBuild(projectId, id)?.build_status;
        if (st === 'Error' || st === 'In Progress') {
            return id;
        }
    }
    return firstLlrUiid;
}

interface BuildRequirementsObject {
    (llrUiid: string, llrData: TableRowData, nodeData: any, nodeId: string): Promise<any>;
}

interface BuildEpicRequirementsObject {
    (hlrUiid: string, hlrData: TableRowData, nodeData: any, nodeId: string): Promise<any>;
}

interface UseVishCoderActionsParams {
    projectId: string | null;
    nodes: Node[];
    setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
    bottomPanelOpen: boolean;
    onToggleBottomPanel?: () => void;
    ensureChatPanelOpen?: () => void;
    openContractModal: (contract: DockerBuildContract) => void;
    isRightPanelOpen: boolean;
    addTerminalLog: (message: string) => void;
    setError: (message: string | null) => void;
    setGenerateLoading: React.Dispatch<React.SetStateAction<boolean>>;
    buildRequirementsObject: BuildRequirementsObject;
    buildEpicRequirementsObject?: BuildEpicRequirementsObject;
    setBuildSummaryData: React.Dispatch<React.SetStateAction<any>>;
    setIsBuildSummaryModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface BuildFeatureContext {
    nodeId: string;
    rowIndex?: number;
}

interface BuildSummaryContext {
    nodeId: string;
    rowIndex?: number;
}

export const useVishCoderActions = ({
    projectId,
    nodes,
    setNodes,
    bottomPanelOpen,
    onToggleBottomPanel,
    ensureChatPanelOpen,
    openContractModal,
    isRightPanelOpen,
    addTerminalLog,
    setError,
    setGenerateLoading,
    buildRequirementsObject,
    buildEpicRequirementsObject,
    setBuildSummaryData,
    setIsBuildSummaryModalOpen,
}: UseVishCoderActionsParams) => {
    const { user } = useAuth();
    
    const generateBuildContract = useCallback(async (
        llrUiid: string,
        rowData: TableRowData,
        nodeData: any,
        nodeId: string
    ): Promise<DockerBuildContract | null> => {
        try {
            const requirementsObject = await buildRequirementsObject(llrUiid, rowData, nodeData, nodeId);
    
            let parsedSettings = {};
            try {
                const savedSettings = getFromStorage<any>('appSettings');
                if (savedSettings) parsedSettings = { ...savedSettings };
                const githubSettings = getProjectGitSettingsFromStorage(projectId);
                if (githubSettings) {
                    parsedSettings = {
                        ...parsedSettings,
                        github: {
                            repo_url: githubSettings.repo_url || '',
                            branch: githubSettings.branch || '',
                            token: githubSettings.token || ''
                        }
                    };
                }
            } catch (error) {
                console.error('Failed to parse app settings:', error);
            }
            const userId = (user as any)?.id || TokenManager.getUserId() || null;
            const contract: DockerBuildContract = {
                metadata: {
                    initiatedBy: 'user',
                    dateTime: new Date().toISOString(),
                    feature_Number: requirementsObject.low_level_requirements.uiid || '0.0.1',
                    user_id: userId,
                    project_id: projectId
                },
                settings: parsedSettings,
                requirements: {
                    low_level_requirements: requirementsObject.low_level_requirements,
                    test_cases: requirementsObject.test_cases,
                    high_level_requirements: requirementsObject.high_level_requirements,
                    user_flow: requirementsObject.user_flow
                }
            };
    
            console.log('=== BUILD CONTRACT FOR DOCKER ===');
            console.log(JSON.stringify(contract, null, 2));
            console.log('=== END BUILD CONTRACT ===');
    
            return contract;
        } catch (error) {
            console.error('Error generating build contract:', error);
            return null;
        }
    }, [buildRequirementsObject, user, projectId]);

    const handleBuildFeature = useCallback(async ({ nodeId, rowIndex = 0 }: BuildFeatureContext): Promise<void> => {
        if (!projectId) {
            console.error('Cannot build feature: No project ID');
            setError('Cannot build feature: No project ID');
            return;
        }

        console.log(`Building feature for nodeId=${nodeId}, rowIndex=${rowIndex}, projectId=${projectId}`);
        setGenerateLoading(true);
        setError(null);
        addTerminalLog(`Building feature for nodeId=${nodeId}, rowIndex=${rowIndex}, projectId=${projectId}`);

        try {
            let node = nodes.find(n => n.id === nodeId);

            if (!node) {
                node = nodes.find(n => {
                    const nodeData = n.data as any;
                    return nodeData?.id === nodeId || nodeData?.uiid === nodeId;
                });
            }

            if (!node) {
                console.error(`Node with ID ${nodeId} not found`);
                console.error('Available node IDs:', nodes.map(n => ({ id: n.id, dataId: (n.data as any)?.id, dataUiid: (n.data as any)?.uiid })));
                throw new Error(`Node with ID ${nodeId} not found`);
            }

            const nodeData = node.data;
            const isFlowNode = node.type && ['userFlowNode', 'highLevelRequirementNode', 'lowLevelRequirementNode', 'testCaseNode'].includes(node.type);

            let rowData: any;
            let rowUiid: string;

            if (isFlowNode) {
                const flowData = nodeData as unknown as FlowNodeData;
                rowData = {
                    uiid: flowData.uiid || flowData.id,
                    id: flowData.id,
                    name: flowData.name,
                    desc: flowData.description || '',
                    originalData: flowData.originalData || flowData
                };
                rowUiid = flowData.uiid || flowData.id;
                console.log(`FlowNode detected: ${rowData.name}, UIID: ${rowUiid}`);
            } else {
                const componentId = (nodeData as any).componentId;
                if (!componentId) {
                    console.error('Component ID not found for this table');
                    throw new Error('Component ID not found for this table');
                }

                const visibleRows: TableRowData[] = (nodeData as any).rows || [];
                const allRows: TableRowData[] = (nodeData as any).allRows || visibleRows;
                const isMinimized = (nodeData as any).isMinimized || false;

                console.log(`Table state: isMinimized=${isMinimized}, visibleRows=${visibleRows.length}, allRows=${allRows.length}`);

                if (rowIndex < 0 || rowIndex >= visibleRows.length) {
                    console.error(`Row index ${rowIndex} is out of bounds (0-${visibleRows.length - 1})`);
                    throw new Error(`Row index ${rowIndex} is out of bounds`);
                }

                rowData = visibleRows[rowIndex];
                console.log(`Found row at index ${rowIndex} in visible rows:`, rowData);

                let effectiveRowIndex = rowIndex;
                if (isMinimized) {
                    const foundIndex = allRows.findIndex(r => r.id === rowData.id);
                    if (foundIndex >= 0) {
                        effectiveRowIndex = foundIndex;
                        console.log(`Row's actual index in allRows: ${effectiveRowIndex}`);
                    }
                }

                rowUiid = rowData.uiid || rowData.id;

                console.log(`BUILDING FEATURE FOR ROW ${rowIndex}:`);
                console.log(`Row name: ${rowData.name}`);
                console.log(`Row UIID: ${rowUiid}`);
                console.log('Original data:', rowData.originalData);
            }

            const buildContract = await generateBuildContract(rowUiid, rowData, nodeData, nodeId);

            if (!buildContract) {
                throw new Error('Failed to generate build contract');
            }

            // Open contract modal directly without opening chat
            openContractModal(buildContract);
            console.log('Contract ready for review. Contract modal opened directly.');
            console.log('Contract details:', buildContract);

            addTerminalLog(`✅ Generated build contract for Docker with ${Object.keys(buildContract.requirements).length} requirement types`);
            addTerminalLog(`📋 Contract includes: LLR=${(buildContract.requirements.low_level_requirements as any)?.name || 'N/A'}, HLR=${(buildContract.requirements.high_level_requirements as any)?.name || 'N/A'}, User Flow=${(buildContract.requirements.user_flow as any)?.name || 'N/A'}, Test Cases=${Array.isArray(buildContract.requirements.test_cases) ? buildContract.requirements.test_cases.length : 0}`);
            addTerminalLog('📄 Full contract logged to console for Docker processing');

            const requirementsObject = await buildRequirementsObject(rowUiid, rowData, nodeData, nodeId);

            const requestId = crypto.randomUUID();
            const timestamp = new Date().toISOString();

            const buildFeatureRequest = {
                request_id: requestId,
                timestamp: timestamp,
                payload: {
                    user_flow: buildContract.requirements.user_flow,
                    high_level_requirement: buildContract.requirements.high_level_requirements,
                    low_level_requirement: buildContract.requirements.low_level_requirements,
                    test_cases: buildContract.requirements.test_cases,
                    tech_stack: requirementsObject.tech_stack
                },
                user_input: '',
                metadata: {
                    request_type: 'code_generation',
                    github: (() => {
                        try {
                            const settings = getProjectGitSettingsFromStorage(projectId);
                            if (settings) {
                                return {
                                    repo_url: settings.repo_url || '',
                                    branch: settings.branch || '',
                                    pr: null
                                };
                            }
                        } catch (error) {
                            console.error('Failed to load GitHub settings:', error);
                        }
                        return { repo_url: '', branch: '', pr: null };
                    })()
                }
            };

            console.log('Build Feature Request:', buildFeatureRequest);
            addTerminalLog(`Created build feature request with ID: ${requestId}`);
            addTerminalLog('✅ Contract building completed successfully');
            addTerminalLog('📋 Contract modal opened for review');
            addTerminalLog('💡 TIP: Review the contract in the modal and send it to VishCoder when ready.');

            if (!isFlowNode && (nodeData as any).title) {
                const originalTitle = (nodeData as any).title;
                setNodes(prevNodes => prevNodes.map(n =>
                    n.id === nodeId
                        ? {
                            ...n,
                            data: {
                                ...n.data,
                                title: `${originalTitle} (Feature Request Sent!)`
                            }
                        }
                        : n
                ));

                setTimeout(() => {
                    setNodes(prevNodes => prevNodes.map(n =>
                        n.id === nodeId
                            ? {
                                ...n,
                                data: {
                                    ...n.data,
                                    title: originalTitle
                                }
                            }
                            : n
                    ));
                }, 3000);
            }
        } catch (error: any) {
            console.error('Error building feature:', error);
            setError(`Failed to build feature: ${error.message || 'Unknown error'}`);
            addTerminalLog(`Error building feature: ${error.message || 'Unknown error'}`);
        } finally {
            setGenerateLoading(false);
        }
    }, [
        projectId,
        nodes,
        setNodes,
        openContractModal,
        addTerminalLog,
        setError,
        setGenerateLoading,
        buildRequirementsObject,
        generateBuildContract
    ]);

    const handleBuildSummary = useCallback(({ nodeId, rowIndex = 0 }: BuildSummaryContext): void => {
        try {
            const node = nodes.find(n => n.id === nodeId);
            if (!node) {
                console.error(`Node with ID ${nodeId} not found`);
                return;
            }

            const nodeData = node.data;
            const isFlowNode = node.type && ['userFlowNode', 'highLevelRequirementNode', 'lowLevelRequirementNode', 'testCaseNode'].includes(node.type);

            let rowData: any;
            let rowUiid: string;

            if (isFlowNode) {
                const flowData = nodeData as unknown as FlowNodeData;
                rowData = {
                    uiid: flowData.uiid || flowData.id,
                    id: flowData.id,
                    name: flowData.name,
                    desc: flowData.description || '',
                    originalData: flowData.originalData || flowData
                };
                rowUiid = flowData.uiid || flowData.id;
                console.log(`FlowNode detected for Build Summary: ${rowData.name}, UIID: ${rowUiid}`);
            } else {
                const visibleRows: TableRowData[] = (nodeData as any).rows || [];

                if (rowIndex < 0 || rowIndex >= visibleRows.length) {
                    console.error(`Row index ${rowIndex} is out of bounds (0-${visibleRows.length - 1})`);
                    return;
                }

                rowData = visibleRows[rowIndex];
                rowUiid = rowData.uiid || rowData.id;
            }

            const summaryData = {
                lowLevelRequirementId: rowUiid,
                status: 'Build In Progress',
                branchLink: 'https://github.com/example/repo/tree/feature-branch',
                prLink: 'https://github.com/example/repo/pull/123',
                documentLinks: [
                    'https://docs.example.com/feature-spec',
                    'https://docs.example.com/api-docs',
                    'https://docs.example.com/user-guide'
                ],
                keyMetrics: 'Response time < 200ms, 99.9% uptime, Error rate < 0.1%',
                dashboardLinks: [
                    'https://grafana.example.com/d/feature-dashboard',
                    'https://datadog.example.com/dashboard/feature-monitoring'
                ],
                alerts: 'CloudWatch Alerts configured for error rate > 1%, response time > 500ms',
                logs: 'CloudWatch Logs: /aws/lambda/feature-function, /aws/ecs/feature-service',
                productManager: 'John Doe',
                devManager: 'Jane Smith'
            };

            setBuildSummaryData(summaryData);
            setIsBuildSummaryModalOpen(true);

            console.log('Build Summary modal opened for:', {
                nodeId,
                rowIndex,
                rowUiid,
                rowData: rowData.name
            });
        } catch (error: any) {
            console.error('Error opening Build Summary modal:', error);
            setError(`Failed to open Build Summary: ${error.message || 'Unknown error'}`);
        }
    }, [nodes, setBuildSummaryData, setError, setIsBuildSummaryModalOpen]);

    const generateBuildEpicContract = useCallback(async (
        hlrUiid: string,
        rowData: TableRowData,
        nodeData: any,
        nodeId: string
    ): Promise<DockerBuildContract | null> => {
        if (!buildEpicRequirementsObject) {
            console.error('buildEpicRequirementsObject is not provided');
            return null;
        }

        try {
            const requirementsObject = await buildEpicRequirementsObject(hlrUiid, rowData, nodeData, nodeId);
    
            let parsedSettings = {};
            try {
                const savedSettings = getFromStorage<any>('appSettings');
                if (savedSettings) parsedSettings = { ...savedSettings };
                const githubSettings = getProjectGitSettingsFromStorage(projectId);
                if (githubSettings) {
                    parsedSettings = {
                        ...parsedSettings,
                        github: {
                            repo_url: githubSettings.repo_url || '',
                            branch: githubSettings.branch || '',
                            token: githubSettings.token || ''
                        }
                    };
                }
            } catch (error) {
                console.error('Failed to parse app settings:', error);
            }
            const userId = (user as any)?.id || TokenManager.getUserId() || null;
            const orderedLlrs = orderedLlrUiidsFromEpicRequirements(
                requirementsObject.high_level_requirements as Record<string, unknown>
            );
            const startAt = computeEpicStartAt(projectId, orderedLlrs);

            const contract: DockerBuildContract = {
                metadata: {
                    initiatedBy: 'user',
                    dateTime: new Date().toISOString(),
                    feature_Number: requirementsObject.high_level_requirements.uiid || '0.0.1',
                    user_id: userId,
                    project_id: projectId
                },
                settings: parsedSettings,
                requirements: {
                    high_level_requirements: requirementsObject.high_level_requirements,
                    ...(startAt !== undefined ? { start_at: startAt } : {})
                },
                contractKind: 'epic'
            };
    
            console.log('=== BUILD EPIC CONTRACT FOR DOCKER ===');
            console.log(JSON.stringify(contract, null, 2));
            console.log('=== END BUILD EPIC CONTRACT ===');
    
            return contract;
        } catch (error) {
            console.error('Error generating build epic contract:', error);
            return null;
        }
    }, [buildEpicRequirementsObject, user, projectId]);

    const handleBuildEpic = useCallback(async ({ nodeId, rowIndex = 0 }: BuildFeatureContext): Promise<void> => {
        if (!projectId) {
            console.error('Cannot build epic: No project ID');
            setError('Cannot build epic: No project ID');
            return;
        }

        if (!buildEpicRequirementsObject) {
            console.error('Cannot build epic: buildEpicRequirementsObject not provided');
            setError('Cannot build epic: buildEpicRequirementsObject not provided');
            return;
        }

        console.log(`Building epic for nodeId=${nodeId}, rowIndex=${rowIndex}, projectId=${projectId}`);
        setGenerateLoading(true);
        setError(null);
        addTerminalLog(`Building epic for nodeId=${nodeId}, rowIndex=${rowIndex}, projectId=${projectId}`);

        try {
            let node = nodes.find(n => n.id === nodeId);

            if (!node) {
                node = nodes.find(n => {
                    const nodeData = n.data as any;
                    return nodeData?.id === nodeId || nodeData?.uiid === nodeId;
                });
            }

            if (!node) {
                console.error(`Node with ID ${nodeId} not found`);
                console.error('Available node IDs:', nodes.map(n => ({ id: n.id, dataId: (n.data as any)?.id, dataUiid: (n.data as any)?.uiid })));
                throw new Error(`Node with ID ${nodeId} not found`);
            }

            const nodeData = node.data;
            const isFlowNode = node.type && ['userFlowNode', 'highLevelRequirementNode', 'lowLevelRequirementNode', 'testCaseNode'].includes(node.type);

            let rowData: any;
            let rowUiid: string;

            if (isFlowNode) {
                const flowData = nodeData as unknown as FlowNodeData;
                rowData = {
                    uiid: flowData.uiid || flowData.id,
                    id: flowData.id,
                    name: flowData.name,
                    desc: flowData.description || '',
                    originalData: flowData.originalData || flowData
                };
                rowUiid = flowData.uiid || flowData.id;
                console.log(`FlowNode detected: ${rowData.name}, UIID: ${rowUiid}`);
            } else {
                const componentId = (nodeData as any).componentId;
                if (!componentId) {
                    console.error('Component ID not found for this table');
                    throw new Error('Component ID not found for this table');
                }

                const visibleRows: TableRowData[] = (nodeData as any).rows || [];
                const allRows: TableRowData[] = (nodeData as any).allRows || visibleRows;
                const isMinimized = (nodeData as any).isMinimized || false;

                console.log(`Table state: isMinimized=${isMinimized}, visibleRows=${visibleRows.length}, allRows=${allRows.length}`);

                if (rowIndex < 0 || rowIndex >= visibleRows.length) {
                    console.error(`Row index ${rowIndex} is out of bounds (0-${visibleRows.length - 1})`);
                    throw new Error(`Row index ${rowIndex} is out of bounds`);
                }

                rowData = visibleRows[rowIndex];
                console.log(`Found row at index ${rowIndex} in visible rows:`, rowData);

                let effectiveRowIndex = rowIndex;
                if (isMinimized) {
                    const foundIndex = allRows.findIndex(r => r.id === rowData.id);
                    if (foundIndex >= 0) {
                        effectiveRowIndex = foundIndex;
                        console.log(`Row's actual index in allRows: ${effectiveRowIndex}`);
                    }
                }

                rowUiid = rowData.uiid || rowData.id;

                console.log(`BUILDING EPIC FOR ROW ${rowIndex}:`);
                console.log(`Row name: ${rowData.name}`);
                console.log(`Row UIID: ${rowUiid}`);
                console.log('Original data:', rowData.originalData);
            }

            const buildContract = await generateBuildEpicContract(rowUiid, rowData, nodeData, nodeId);

            if (!buildContract) {
                throw new Error('Failed to generate build epic contract');
            }

            // Open contract modal directly without opening chat
            openContractModal(buildContract);
            console.log('Epic contract ready for review. Contract modal opened directly.');
            console.log('Epic contract details:', buildContract);

            const llrCount = buildContract.requirements.high_level_requirements?.low_level_requirements 
                ? Object.keys(buildContract.requirements.high_level_requirements.low_level_requirements).length 
                : 0;
            addTerminalLog(`✅ Generated epic build contract for Docker with HLR and ${llrCount} LLRs`);
            addTerminalLog(`📋 Epic contract includes: HLR=${buildContract.requirements.high_level_requirements?.name || 'N/A'}`);
            addTerminalLog('📄 Full epic contract logged to console for Docker processing');

            addTerminalLog('✅ Epic contract building completed successfully');
            addTerminalLog('📋 Contract modal opened for review');
            addTerminalLog('💡 TIP: Review the epic contract in the modal and send it to VishCoder when ready.');

            if (!isFlowNode && (nodeData as any).title) {
                const originalTitle = (nodeData as any).title;
                setNodes(prevNodes => prevNodes.map(n =>
                    n.id === nodeId
                        ? {
                            ...n,
                            data: {
                                ...n.data,
                                title: `${originalTitle} (Epic Request Sent!)`
                            }
                        }
                        : n
                ));

                setTimeout(() => {
                    setNodes(prevNodes => prevNodes.map(n =>
                        n.id === nodeId
                            ? {
                                ...n,
                                data: {
                                    ...n.data,
                                    title: originalTitle
                                }
                            }
                            : n
                    ));
                }, 3000);
            }
        } catch (error: any) {
            console.error('Error building epic:', error);
            setError(`Failed to build epic: ${error.message || 'Unknown error'}`);
            addTerminalLog(`Error building epic: ${error.message || 'Unknown error'}`);
        } finally {
            setGenerateLoading(false);
        }
    }, [
        projectId,
        nodes,
        setNodes,
        openContractModal,
        addTerminalLog,
        setError,
        setGenerateLoading,
        buildEpicRequirementsObject,
        generateBuildEpicContract
    ]);

    return {
        handleBuildFeature,
        handleBuildSummary,
        handleBuildEpic,
        generateBuildContract,
        generateBuildEpicContract
    };
};

